<script setup>
import { ref } from 'vue'
import { Clock, Users } from 'lucide-vue-next'
import { router } from '@inertiajs/vue3'
import axios from 'axios'
import { useRecordSgaGoal } from '@/composables/useRecordSgaGoal'

const handleClick = (e) => {
    if (e.target.closest('a, button, input, textarea')) return
    if (!props.casting.slug) return
    router.visit(route(props.routePrefix + '.studio.casting.view', { slug: props.casting.slug }))
}

const STAR_ICON_URL = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const props = defineProps({
    casting: { type: Object, required: true },
    routePrefix: { type: String, default: 'members2' },
    // 'requested' shows status + expiry + stars received
    // 'backed'    shows "You gave X stars"
    mode: { type: String, default: 'requested' },
    requiresAgeVerification: { type: Boolean, default: false },
    isUserLoggedIn: { type: Boolean, default: false },
    userBalance: { type: Number, default: 0 },
    supportCost: { type: Number, default: 1 },
})

const emit = defineEmits(['updateBalance', 'openBuyStars', 'joinClick'])

// ── Voting ────────────────────────────────────────────────────────────────────
const localVote     = ref(props.casting.user_vote ?? 0)
const localNetScore = ref(props.casting.net_score ?? 0)
const voteLoading   = ref(false)
const voteError     = ref(null)

const isOwner = props.casting.is_own ?? false
const { recordSgaGoal } = useRecordSgaGoal()

const submitVote = async (voteValue) => {
    if (isOwner) return

    if (!props.isUserLoggedIn) {
        emit('joinClick', voteValue === 1 ? 'Boost' : 'Not_For_Me')
        return
    }

    if (voteLoading.value) return

    voteError.value = null

    const prevVote     = localVote.value
    const prevNetScore = localNetScore.value
    const newVote      = prevVote === voteValue ? 0 : voteValue
    // Optimistic update
    localVote.value     = newVote
    localNetScore.value = prevNetScore + (newVote - prevVote)
    voteLoading.value   = true

    try {
        const response = await axios.post(route('members2.reels.casting.vote', { id: props.casting.id }), { vote: voteValue })
        localVote.value     = response.data.user_vote
        localNetScore.value = response.data.net_score

        if (response.data.remaining_balance !== undefined) {
            emit('updateBalance', response.data.remaining_balance)
        }

        if (response.data.user_vote !== 0) {
            recordSgaGoal('vote_pitch', { casting_id: props.casting.id })
        }
    } catch (e) {
        localVote.value     = prevVote
        localNetScore.value = prevNetScore
        voteError.value = e.response?.data?.message || 'Something went wrong.'
        if (e.response?.data?.message?.includes('enough Stars')) {
            emit('openBuyStars')
        }
    } finally {
        voteLoading.value = false
    }
}

const formatNumber = (n) => n ? Number(n).toLocaleString('en-US') : '0'

const formatCastingExpiry = (isoDate) => {
    if (!isoDate) return null
    const diff = new Date(isoDate) - Date.now()
    if (diff <= 0) return 'Expired'
    const days = Math.floor(diff / 86400000)
    if (days > 0) return `${days}d left`
    return `${Math.floor(diff / 3600000)}h left`
}

const castingStatusLabel = (status) => {
    const map = { 0: 'Draft', 1: 'Open', 2: 'Talent Tagged', 3: 'Expired', 4: 'In Production', 5: 'Released' }
    return map[status] ?? String(status)
}

const castingStatusClass = (status) => {
    if (status === 8) return 'text-green-300 bg-green-500/20 border-green-400/40' // Race Over
    if (status === 7) return 'text-orange-300 bg-orange-500/20 border-orange-400/40' // In The Race
    if (status === 6) return 'text-purple-400 bg-purple-500/10 border-purple-500/20' // Now Casting
    if (status === 5) return 'text-blue-400 bg-blue-500/10 border-blue-500/20'   // Released
    if (status === 4) return 'text-orange-300 bg-orange-500/20 border-orange-400/40' // Fulfilled
    if (status === 3) return 'text-white/30 bg-white/5 border-white/10'           // Expired
    if (status === 1) return 'text-amber-400 bg-amber-500/10 border-amber-500/20' // Open
    return 'text-white/50 bg-white/5 border-white/10' // Draft (0) + fallback
}

const descriptionExpanded = ref(false)

const timeAgo = (dateStr) => {
    if (!dateStr) return ''
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000)
    if (diff < 60)      return 'just now'
    if (diff < 3600)    return `${Math.floor(diff / 60)}m ago`
    if (diff < 86400)   return `${Math.floor(diff / 3600)}h ago`
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}
</script>

<template>
    <div
        class="rounded-xl border transition-colors p-4 flex flex-col gap-3 cursor-pointer"
        :class="casting.status === 3
            ? 'bg-zinc-900/30 border-white/[0.04] opacity-60 grayscale-[40%]'
            : 'bg-zinc-900/60 border-white/[0.06] hover:border-white/[0.12]'"
        @click="handleClick"
    >

        <!-- Performers -->
        <template v-if="casting.performers.length === 1">
            <div class="flex items-center gap-3">
                <a
                    v-if="!requiresAgeVerification"
                    :href="casting.performers[0].slug ? route(`${routePrefix}.performer.show`, { slug: casting.performers[0].slug }) : null"
                    class="w-12 h-12 rounded-full overflow-hidden bg-zinc-800 ring-2 ring-zinc-900 flex-shrink-0 hover:ring-white/30 transition-all"
                >
                    <img :src="casting.performers[0].thumbnail_url" :alt="casting.performers[0].name" class="w-full h-full object-cover object-top" loading="lazy" />
                </a>
                <div class="min-w-0">
                    <h3 class="text-white text-sm font-semibold leading-snug line-clamp-2">{{ casting.title }}</h3>
                    <p class="text-white/40 text-xs mt-0.5 truncate">{{ casting.performers[0].name }}</p>
                </div>
            </div>
        </template>

        <template v-else-if="casting.performers.length > 1">
            <div class="flex gap-2 flex-wrap">
                <a
                    v-for="performer in casting.performers.slice(0, 4)"
                    :key="performer.id"
                    :href="performer.slug ? route(`${routePrefix}.performer.show`, { slug: performer.slug }) : null"
                    class="w-9 h-9 rounded-full overflow-hidden bg-zinc-800 ring-2 ring-zinc-900 flex-shrink-0 hover:ring-white/30 transition-all"
                >
                    <img :src="performer.thumbnail_url" :alt="performer.name" class="w-full h-full object-cover object-top" loading="lazy" />
                </a>
                <div v-if="casting.performers.length > 4" class="w-9 h-9 rounded-full bg-zinc-800 ring-2 ring-zinc-900 flex items-center justify-center flex-shrink-0">
                    <span class="text-white/50 text-xs font-bold">+{{ casting.performers.length - 4 }}</span>
                </div>
            </div>
            <div>
                <h3 class="text-white text-sm font-semibold leading-snug line-clamp-2">{{ casting.title }}</h3>
                <p class="text-white/40 text-xs mt-0.5">{{ casting.performers.map(p => p.name).join(', ') }}</p>
            </div>
        </template>

        <!-- No performers - show performer description -->
        <template v-else>
            <div>
                <h3 class="text-white text-sm font-semibold leading-snug line-clamp-2">{{ casting.title }}</h3>
                <p v-if="casting.performer_description" class="text-white/40 text-xs mt-0.5 line-clamp-2">{{ casting.performer_description }}</p>
            </div>
        </template>

        <!-- Description -->
        <p v-if="casting.description" class="text-white/50 text-xs leading-relaxed">
            {{ descriptionExpanded ? casting.description : casting.description.slice(0, 200) }}{{ !descriptionExpanded && casting.description.length > 200 ? '… ' : ' ' }}<button
                v-if="casting.description.length > 200"
                @click="descriptionExpanded = !descriptionExpanded"
                class="text-sky-400/80 hover:text-sky-300 text-xs font-semibold transition-colors"
            >{{ descriptionExpanded ? 'Read less' : 'Read more' }}</button>
        </p>

        <!-- Director's Notes -->
        <div v-if="casting.director_notes" class="px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <p class="text-white/30 text-[10px] font-bold uppercase tracking-wider mb-1">Director's Notes</p>
            <p class="text-white/50 text-xs leading-relaxed line-clamp-2">{{ casting.director_notes }}</p>
        </div>

        <!-- Format / Setting / Tags pills -->
        <div v-if="casting.format || casting.setting?.length || casting.tags?.length" class="flex flex-wrap items-center gap-1.5">
            <span
                v-if="casting.format"
                class="px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 text-[11px] font-semibold"
            >{{ casting.format }}</span>
            <span
                v-for="s in (casting.setting || [])"
                :key="'s-' + s"
                class="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-[11px] font-medium"
            >{{ s }}</span>
            <span
                v-for="tag in (casting.tags || [])"
                :key="'t-' + (tag.id ?? tag)"
                class="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-[11px] font-medium"
            >{{ tag.name ?? tag }}</span>
        </div>

        <!-- Linked auditions -->
        <span
            v-if="casting.auditions?.length"
            class="inline-flex items-center gap-1.5 self-start px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-[11px] font-bold shadow shadow-emerald-500/10"
        >
            <span class="relative flex h-1.5 w-1.5">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span class="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
            </span>
            {{ casting.auditions.length }} audition{{ casting.auditions.length !== 1 ? 's' : '' }} in the race
        </span>

        <!-- Progress bar (stars) — hidden, kept for reference (replaced by vote system) -->
        <!--
        <div v-if="casting.star_goal > 0" class="pt-2 border-t border-white/[0.06]">
            <div class="flex items-center justify-between mb-1.5">
                <span v-if="casting.stars_received / casting.star_goal >= 0.75" class="text-[11px] font-semibold text-orange-400">🔥 Trending toward production</span>
                <span v-else class="text-[11px] text-white/30"></span>
                <span class="text-[11px] text-white/40 font-medium">{{ formatNumber(casting.stars_received) }} / {{ formatNumber(casting.star_goal) }} stars</span>
            </div>
            <div class="h-1.5 bg-white/[0.08] rounded-full overflow-hidden">
                <div
                    class="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all duration-500"
                    :style="{ width: Math.min(100, Math.round(casting.stars_received / casting.star_goal * 100)) + '%' }"
                />
            </div>
        </div>
        -->

        <!-- Footer -->
        <div class="flex items-center justify-between gap-2">
            <div class="flex flex-wrap gap-1.5">
                <!-- requested-only badges -->
                <template v-if="mode === 'requested'">
                    <!--<span
                        v-if="casting.expires_at && casting.status !== 4"
                        class="px-2 py-0.5 rounded-full bg-white/[0.08] text-white/60 text-[11px] font-medium flex items-center gap-1"
                    >
                        <Clock class="w-3 h-3" />
                        {{ formatCastingExpiry(casting.expires_at) }}
                    </span>-->
                    <span :class="['px-2 py-0.5 rounded-full border text-[11px] font-medium', castingStatusClass(casting.status)]">
                        {{ castingStatusLabel(casting.status) }}
                    </span>
                </template>

                <span
                    v-if="casting.backers_count > 0"
                    class="px-2 py-0.5 rounded-full bg-white/[0.08] text-white/60 text-[11px] font-medium flex items-center gap-1"
                >
                    <Users class="w-3 h-3" />
                    {{ casting.backers_count }} supporter{{ casting.backers_count !== 1 ? 's' : '' }}
                </span>

                <!-- Revised by Staff badge -->
                <span
                    v-if="casting.revised_by_admin"
                    class="px-2 py-0.5 rounded-full bg-emerald-600/90 text-white border border-emerald-400/30 text-[11px] font-bold"
                >
                    Revised by Staff
                </span>
            </div>

            <!-- Stars received / given — hidden, kept for reference (replaced by vote system) -->
            <!--
            <div v-if="mode === 'requested'" class="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold bg-amber-500/20 text-amber-400 flex-shrink-0">
                <img :src="STAR_ICON_URL" alt="" class="w-3.5 h-3.5" />
                {{ formatNumber(casting.stars_received) }}
            </div>
            <div v-else class="flex items-center gap-2 ml-auto">
                <span class="text-white/30 text-[11px]">You gave</span>
                <span class="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold bg-amber-500/20 text-amber-400">
                    <img :src="STAR_ICON_URL" alt="" class="w-3.5 h-3.5" />
                    {{ formatNumber(casting.stars_given) }}
                </span>
            </div>
            -->

            <!-- Boost / Not For Me — hidden for owner -->
            <div v-if="!isOwner" class="flex items-center gap-1 flex-shrink-0">
                <!-- Boost -->
                <button
                    @click.stop="submitVote(1)"
                    :disabled="voteLoading"
                    :class="[
                        'flex items-center gap-1 px-2 py-1 rounded-lg border text-[11px] font-semibold transition-all active:scale-95',
                        localVote === 1
                            ? 'bg-blue-500/20 border-blue-500/40 text-blue-300'
                            : 'bg-transparent border-white/10 text-white/40 hover:border-blue-500/30 hover:text-blue-300',
                    ]"
                    title="Boost this pitch with Stars"
                >
                    <img :src="STAR_ICON_URL" alt="" class="w-2.5 h-2.5 flex-shrink-0" />
                    {{ localVote === 1 ? 'Boosted' : 'Boost' }}<template v-if="localVote !== 1"><span class="opacity-50">· {{ supportCost === -1 ? '?' : supportCost }}</span></template>
                </button>

                <!-- Net score -->
                <span
                    class="text-[11px] font-bold tabular-nums w-5 text-center"
                    :class="{
                        'text-blue-400':   localNetScore > 0,
                        'text-orange-400': localNetScore < 0,
                        'text-white/30':   localNetScore === 0,
                    }"
                >{{ localNetScore }}</span>

                <!-- Not For Me -->
                <button
                    @click.stop="submitVote(-1)"
                    :disabled="voteLoading"
                    :class="[
                        'flex items-center gap-1 px-2 py-1 rounded-lg border text-[11px] font-semibold transition-all active:scale-95',
                        localVote === -1
                            ? 'bg-orange-500/20 border-orange-500/40 text-orange-300'
                            : 'bg-transparent border-white/10 text-white/40 hover:border-orange-500/30 hover:text-orange-300',
                    ]"
                    title="Mark this pitch as not for me"
                >
                    <img :src="STAR_ICON_URL" alt="" class="w-2.5 h-2.5 flex-shrink-0 opacity-50" />
                    Not for me<template v-if="localVote !== -1"><span class="opacity-50">· {{ supportCost === -1 ? '?' : supportCost }}</span></template>
                </button>
            </div>

            <!-- Net score for own pitches -->
            <span
                v-else
                class="text-[11px] font-bold tabular-nums flex-shrink-0"
                :class="{
                    'text-blue-400':   localNetScore > 0,
                    'text-orange-400': localNetScore < 0,
                    'text-white/30':   localNetScore === 0,
                }"
            >
                <img :src="STAR_ICON_URL" alt="" class="inline w-2.5 h-2.5 mr-0.5" />{{ localNetScore }}
            </span>
        </div>

        <!-- Vote error (e.g. insufficient balance) -->
        <p v-if="voteError" class="text-red-400 text-[11px]">{{ voteError }}</p>

        <p class="text-white/20 text-[11px]">{{ timeAgo(mode === 'backed' ? casting.backed_at : casting.created_at) }}</p>
    </div>
</template>
